import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";

import CustomModal from "components/common/Modal";
import Button from "components/common/Button";
import DatePicker from "components/common/DatePicker";
import { AlertType } from "components/common/Snackbar";

import { unassignEmployeeFromACustomer, unassignEmployeeFromACustomerV2 } from "services/Admin/Dashboard";
import { showSnackbar } from "store/actions/Utility";
import { GetEmployeeDetails } from "store/actions/GetEmployeeDetails";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import "./UnassignEmployee.scss";

const getValidationSchema = (futureTerminationUnassignment) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  return Yup.object().shape({
    endDate: futureTerminationUnassignment
      ? Yup.date().required("Termination date is required")
      : Yup.date().required("Termination date is required").max(today, "Termination date cannot be in the future"),
  });
};

function UnassignedEmployeeModal({
  name,
  companyName,
  open,
  closeModal,
  employeeId,
  fetchEmployeeDetailsByIDV2,
  scheduledAction,
}) {
  const dispatch = useDispatch();
  const { anniversaryBasedTimeOff, futureTerminationUnassignment } = useFlags();
  const [unassigningEmployee, setUnassigningEmployee] = useState(false);

  const isFutureTermination = scheduledAction !== null && futureTerminationUnassignment;

  const scheduleTimezone = scheduledAction?.employeeTimezone.split("/")[1];
  const scheduledDate = moment(scheduledAction?.scheduledDate).format("Do MMMM YYYY");

  const methods = useForm({
    resolver: yupResolver(getValidationSchema(futureTerminationUnassignment)),
    mode: "onChange",
    defaultValues: {
      endDate: null,
    },
  });

  useEffect(() => {
    if (open && anniversaryBasedTimeOff) {
      methods.reset({
        endDate: new Date(),
      });
    }
  }, [open, methods, anniversaryBasedTimeOff]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const handleEmployeeUnassignment = async () => {
    setUnassigningEmployee(true);
    try {
      const res =
        anniversaryBasedTimeOff && futureTerminationUnassignment
          ? await unassignEmployeeFromACustomerV2(employeeId, {
              endDate: moment(methods.getValues().endDate).format("YYYY-MM-DD"),
            })
          : await unassignEmployeeFromACustomer(employeeId);

      setUnassigningEmployee(false);
      if (res?.payload?.error?.length > 0) {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: res?.payload?.error?.[0]?.description || res?.payload?.error?.[0]?.msg || "An error occurred.",
            dispatch,
          })
        );
        closeModal();
        return;
      }

      dispatch(
        GetEmployeeDetails({
          employeeID: employeeId,
          request: `employee/${employeeId}`,
        })
      );

      fetchEmployeeDetailsByIDV2();
      closeModal();
      dispatch(showSnackbar({ type: AlertType.success, message: "Employee unassigned successfully", dispatch }));
    } catch (error) {
      dispatch(
        showSnackbar({
          type: AlertType.error,
          message: "An Error Occured",
        })
      );
    }
    setUnassigningEmployee(false);
    closeModal();
  };

  const onSubmit = async () => {
    await handleEmployeeUnassignment();
  };

  return (
    <CustomModal
      open={open}
      onClose={closeModal}
      width="474px"
      padding={anniversaryBasedTimeOff ? "24px" : undefined}
      borderRadius={anniversaryBasedTimeOff ? "12px" : undefined}
    >
      {anniversaryBasedTimeOff ? (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-1">
              <div className="flex justify-start">
                <div className="failure-warn-icon">
                  <WarningAmberIcon />
                </div>
              </div>
              <div className="unassign-employee-modal-content-box">
                <div className="unassign-employee-title">
                  {isFutureTermination ? "Unassignment Scheduled." : "Unassign Employee"}
                </div>
                {isFutureTermination ? (
                  <div className="unassign-employee-prompt">
                    This employee is scheduled to be unassigned at{" "}
                    <span className="unassign-employee-detail">
                      "{scheduledDate} 12:00AM ({scheduleTimezone} time zone)"
                    </span>
                    . Editing the date and saving will change the unassignment date for the employee.
                  </div>
                ) : (
                  <div className="unassign-employee-prompt">
                    Are you sure you want to unassign <span className="unassign-employee-detail">{name}</span> from{" "}
                    <span className="unassign-employee-detail">{companyName}</span>? This will:
                  </div>
                )}
                <div className="unassign-employee-warning-box">
                  <ul className="unassign-employee-points-list">
                    <li>Permanently unassign the employee from this customer</li>
                    <li>Delete all salary details of the employee</li>
                    <li>Reset time-off balances of the employee</li>
                  </ul>
                </div>
                <div className="para-body-m-medium unassign-employee-danger">This action cannot be reversed.</div>
              </div>
              <div>
                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Confirm employee termination date"
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message}
                      {...(!futureTerminationUnassignment && { maxDate: new Date() })}
                    />
                  )}
                />
              </div>
              <div className="flex justify-end gap-1 py-05">
                <Button label="Cancel" color="inherit-text" variant="outlined" onClick={closeModal} />
                <Button
                  label="Unassign"
                  isLoading={unassigningEmployee}
                  variant="contained"
                  color="error"
                  type="submit"
                />
              </div>
            </div>
          </form>
        </FormProvider>
      ) : (
        <div>
          <div className="failure-warning-icon">
            <WarningAmberIcon />
          </div>
          <div className="heading-h2-semibold pt-1">Unassign Employee</div>
          <div>
            <span className="para-body-m-regular pt-025">Are you sure you want to unassign</span>{" "}
            <span className="para-body-m-semibold employee-success-name capitalize">{name}</span>{" "}
            <span className="para-body-m-regular pt-025">from</span>{" "}
            <span className="para-body-m-semibold employee-success-name capitalize">{companyName}</span>
            <span className="para-body-m-regular pt-025">?</span>
          </div>
          <div className="float-right flex gap-05 pt-20p">
            <Button label="Cancel" color="inherit-text" variant="outlined" onClick={closeModal} />
            <Button
              label="Unassign Now"
              isLoading={unassigningEmployee}
              variant="contained"
              color="error"
              onClick={handleEmployeeUnassignment}
            />
          </div>
        </div>
      )}
    </CustomModal>
  );
}

UnassignedEmployeeModal.propTypes = {
  name: PropTypes.string.isRequired,
  companyName: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  employeeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchEmployeeDetailsByIDV2: PropTypes.func.isRequired,
};

export default UnassignedEmployeeModal;
